'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAdminBrokenLinkStats,
  listAdminBrokenLinks,
  updateAdminBrokenLinkStatus,
  type AdminBrokenLinkStatus,
  type AdminBrokenLinksListParams,
} from '@nomiss/services';

export const adminBrokenLinksQueryKeys = {
  all: ['admin', 'broken-links'] as const,
  list: (filters: AdminBrokenLinksListParams) =>
    [...adminBrokenLinksQueryKeys.all, 'list', filters] as const,
  stats: () => [...adminBrokenLinksQueryKeys.all, 'stats'] as const,
};

export function useAdminBrokenLinksList(
  filters: AdminBrokenLinksListParams,
  options?: { enabled?: boolean },
) {
  const adminBrokenLinksListQuery = useQuery({
    queryKey: adminBrokenLinksQueryKeys.list(filters),
    queryFn: () => listAdminBrokenLinks(filters),
    enabled: options?.enabled ?? true,
  });

  return { adminBrokenLinksListQuery };
}

export function useAdminBrokenLinkStats(options?: { enabled?: boolean }) {
  const adminBrokenLinkStatsQuery = useQuery({
    queryKey: adminBrokenLinksQueryKeys.stats(),
    queryFn: () => getAdminBrokenLinkStats(),
    enabled: options?.enabled ?? true,
  });

  return { adminBrokenLinkStatsQuery };
}

export function useUpdateAdminBrokenLinkStatus() {
  const queryClient = useQueryClient();

  const updateAdminBrokenLinkStatusMutation = useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string;
      status: AdminBrokenLinkStatus;
    }) => updateAdminBrokenLinkStatus(id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adminBrokenLinksQueryKeys.all,
      });
    },
  });

  return { updateAdminBrokenLinkStatusMutation };
}
